import {
  buildCommonValidationMessages,
  emailField,
  optionalString,
  phoneField,
  requiredString,
  withMaxLength,
} from '@/utils/validation/schemaHelpers';

import type { FieldDef, FieldGroup } from './types';
import type { DefaultOptionType } from 'antd/es/select';
import type { TFunction } from 'i18next';

type PersonalInfoFieldsOptions = {
  t: TFunction;
  title?: string;
  disableFields?: boolean;
  disableDepartment?: boolean;
  companyOptions?: DefaultOptionType[];
  onCompanySearch?: (value: string) => void;
  onCompanyChange?: (companyId: string) => void;
  includeHiddenIds?: boolean;
  phoneOptional?: boolean;
  fieldTestIds?: Partial<Record<'name' | 'email', string>>;
};

function buildPersonalInfoFieldDefs(
  options: PersonalInfoFieldsOptions,
): FieldDef[] {
  const {
    t,
    disableFields,
    disableDepartment,
    phoneOptional = false,
  } = options;
  const messages = buildCommonValidationMessages(t);

  const fields: FieldDef[] = [
    {
      name: 'name',
      validation: withMaxLength(
        requiredString(messages.required),
        128,
        messages.maxLength(128),
      ),
      ui: {
        type: 'text',
        label: t('NAME_LABEL'),
        componentProps: options.fieldTestIds?.name
          ? { 'data-testid': options.fieldTestIds.name }
          : undefined,
      },
    },
  ];

  if (options.includeHiddenIds !== false) {
    fields.push(
      {
        name: 'company_id',
        validation: optionalString(),
        ui: { type: 'hidden' },
      },
      {
        name: 'client_id',
        validation: optionalString(),
        ui: { type: 'hidden' },
      },
    );
  }

  fields.push(
    {
      name: 'title',
      validation: withMaxLength(
        requiredString(messages.required),
        255,
        messages.maxLength(255),
      ),
      ui: {
        type: 'text',
        label: t('TITLE_TEXT'),
        colSpan: 12,
      },
    },
    {
      name: 'company',
      validation: requiredString(messages.required),
      ui: {
        type: 'select',
        label: t('COMPANY_TITLE'),
        colSpan: 12,
        componentProps: {
          disabled: disableFields,
          showSearch: {
            filterOption: false,
            onSearch: options.onCompanySearch,
          },
          allowClear: true,
          defaultActiveFirstOption: false,
          notFoundContent: null,
          options: options.companyOptions ?? [],
          onChange: (value: string) => {
            options.onCompanyChange?.(value ? String(value) : '');
          },
        },
      },
    },
    {
      name: 'department',
      validation: requiredString(messages.required),
      ui: {
        type: 'text',
        label: t('DEPARTMENT_TITLE'),
        componentProps: { disabled: disableDepartment },
      },
    },
    {
      name: 'email',
      validation: emailField(messages),
      ui: {
        type: 'text',
        label: t('EMAIL'),
        colSpan: 12,
        componentProps: {
          disabled: disableFields,
          ...(options.fieldTestIds?.email
            ? { 'data-testid': options.fieldTestIds.email }
            : {}),
        },
      },
    },
    {
      name: 'phone',
      validation: phoneField(messages, !phoneOptional),
      ui: {
        type: 'text',
        label: phoneOptional
          ? `${t('PHONE')} (${t('OPTIONAL_TEXT')})`
          : t('PHONE'),
        colSpan: 12,
      },
    },
  );

  return fields;
}

export function buildPersonalInfoGroup(
  options: PersonalInfoFieldsOptions,
): FieldGroup {
  return {
    key: 'personal-info',
    title: options.title,
    fields: buildPersonalInfoFieldDefs(options),
  };
}
